/**
 * VideoModal — responsive iframe overlay.
 * Used together with `WatchVideoButton` (this file also exports it).
 *
 * Opens a centred dimmed-backdrop modal with a 16:9 iframe (Tailwind
 * `aspect-video`). Closes on backdrop click, X button, or Escape key.
 * Works across phone / tablet / desktop via responsive width caps.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, PlayCircle } from 'lucide-react';

export const VideoModal = ({ videoUrl, onClose }) => {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!videoUrl) return null;

  // Portal the modal to <body> so its `fixed inset-0` overlay escapes any
  // ancestor with a CSS `transform` / `filter` / `perspective` (which would
  // otherwise become the containing block for `position: fixed`, shrinking
  // the dim layer to the central content area instead of the whole viewport).
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="video-modal"
    >
      <div
        className="relative bg-black rounded-lg shadow-2xl w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 bg-white text-gray-800 hover:bg-gray-100 rounded-full p-1.5 shadow-lg"
          aria-label="Close video"
          data-testid="video-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        {/* 16:9 responsive container — Tailwind's aspect-video keeps ratio
            on every screen size; iframe absolutely fills it. */}
        <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
          <iframe
            src={videoUrl}
            title="Explainer video"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="video-modal-iframe"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * WatchVideoButton — compact inline trigger placed next to the answer text.
 * Renders nothing when no videoUrl is provided.
 */
export const WatchVideoButton = ({ videoUrl, onClick, className = '' }) => {
  if (!videoUrl) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors ${className}`}
      title="Watch the explainer video for this question"
      data-testid="watch-video-btn"
    >
      <PlayCircle className="w-3.5 h-3.5" />
      Watch video
    </button>
  );
};

export default VideoModal;
